import React from 'react';
import store from '../store';
import { Navigate } from 'react-router-dom';
import { rrNavigate } from '@router-compat';
import { doGet, doPost } from './DataManager';
import { toMoment, toMomentStrict } from './utils/dates';
import { getUnanimousValue } from './logic/groupAggregation';
import { effectiveGroupId } from './logic/dailyTripOccurrences';
import { collapseGroupLegs, groupDurationParts } from './logic/occupancy/occupancySegments';
import dayjs from 'dayjs';

/**
 * Robust price parsing utility that handles various price formats
 * @param {any} priceValue - The price value to parse (string, number, undefined, null)
 * @returns {number} - The parsed price as a number
 */
export function parsePrice(priceValue) {
  if (priceValue === null || priceValue === undefined || priceValue === '') {
    return 0;
  }

  let prezzo;
  if (typeof priceValue === 'string') {
    // Remove currency symbols, commas, and whitespace
    const cleaned = priceValue.replace('€', '').replace(',', '.').trim();
    prezzo = parseFloat(cleaned);
  } else if (typeof priceValue === 'number') {
    prezzo = priceValue;
  } else {
    // Handle other types (objects, etc.)
    prezzo = parseFloat(priceValue);
  }

  // Final validation
  return isNaN(prezzo) ? 0 : prezzo;
}

const getState = function () {
  return store.getState();
};

export function goTo(path) {
  return <Navigate to={path} />;
}

export function getGlobalState() {
  return getState();
}

export function renderIf(condition, dom) {
  if (condition) {
    return dom;
  }
  return '';
}

export async function getCompanies() {
  const sediProm = window.firebase.firestore().collection('users').orderBy('sede', 'desc').get();
  const companiesProm = window.firebase
    .firestore()
    .collection('companies')
    .where('active', '==', true)
    .get();
  const results = await Promise.all([sediProm, companiesProm]);
  const snap = results[1];
  const snapSedi = results[0];
  const companies = [];
  if (!snap.empty) {
    for (let doc of snap.docs) {
      const company = doc.data();
      company.id = doc.id;
      companies.push(company);
    }
  }
  const ins = [];
  if (!snapSedi.empty) {
    for (let doc of snapSedi.docs) {
      const user = doc.data();
      if (ins.indexOf(user.sede) == -1) {
        ins.push(user.sede);
        companies.push({
          active: true,
          label: user.sede,
          code: user.sede,
          id: user.sede,
        });
      }
    }
  }
  companies.sort((a, b) => {
    if (a.label > b.label) {
      return 1;
    } else {
      return -1;
    }
  });
  return companies;
}

export function isInRole(arr) {
  const state = getState();
  if (arr && arr.indexOf(state.role) != -1) {
    return true;
  }
  return false;
}

export function goToPath(path, state = null) {
  rrNavigate(path, state ? { state } : undefined);
}

function getContratto(cid) {
  return new Promise(function (resolve) {
    if (cid) {
      window.firebase
        .firestore()
        .collection('contratti')
        .where('codice', '==', cid)
        .get()
        .then(function (snap) {
          if (!snap.exists) {
            for (let doc of snap.docs) {
              const contratto = doc.data();
              resolve(contratto);
              break;
            }
          }
        })
        .catch(function (err) {
          console.log(err);
          resolve(null);
        });
    } else {
      resolve(null);
    }
  });
}

export async function getPrezzoFermoAuto(order) {
  return new Promise(async function (resolve) {
    let prezzo = 0;
    if (order.fermo_auto) {
      try {
        const contratto = await getContratto(order?.contratto);
        //const fermo_incluso = contratto.minuti_fermo_auto || 0;
        if (contratto) {
          //Senza contratto non saprei come prezzarlo
          const slot_da_pagare = Math.ceil(order.fermo_auto / 30);
          const prezzo_slot = 17.5;
          prezzo = slot_da_pagare * prezzo_slot;
        }
      } catch (err) {
        console.log(err);
        resolve(prezzo);
      }
    }
    resolve(prezzo);
  });
}

export async function getTripData(id) {
  return new Promise(async function (resolve, reject) {
    if (id && id !== '') {
      doGet('/orders/getOrder', { oid: id })
        .then((order) => {
          resolve(order);
        })
        .catch((err) => {
          reject(err);
        });
    } else {
      reject('id mancante');
    }
  });
}

/**
 * Unione dei periodi di validità di un gruppo di pianificati.
 *
 * Un estremo mancante su anche UN solo membro rende il gruppo illimitato da
 * quel lato: se un passeggero non ha fine, il giro continua. Perciò `null` vince
 * su qualunque data, e fra le date si prende la più larga (min per l'inizio, max
 * per la fine).
 *
 * @param {Array<{start_date?: *, end_date?: *}>} group
 * @returns {{start_date: string|null, end_date: string|null}}
 */
function mergeValidityRange(group) {
  const widest = (key, pick) => {
    let best = null;
    for (const trip of group) {
      const day = toMomentStrict(trip?.[key]);
      if (!day) return null; // un membro senza estremo → gruppo illimitato di qua
      if (!best || pick(day, best)) best = day;
    }
    return best ? best.format('YYYY-MM-DD') : null;
  };

  return {
    start_date: widest('start_date', (d, best) => d.isBefore(best, 'day')),
    end_date: widest('end_date', (d, best) => d.isAfter(best, 'day')),
  };
}

/**
 * Merges the daily trips that ride together into one item per group.
 *
 * `options.date` (YYYY-MM-DD) merges them **as they ride that day**: the group
 * of a trip on a date can be changed by an exception (`_overrides.{date}.group_id`),
 * so a trip taken out of its group for one day must come out of the merge for
 * that day too — otherwise the board keeps drawing it inside the group and the
 * exception is visible only as a badge. Without a date the series' `group_id`
 * is used, as before (calendar, trip detail).
 */
// Some companies store the passenger as `nome`/`cognome` only, without the
// denormalised `nome_passeggero`: a group label built from the latter alone
// came out as ", , ,".
const dailyTripPassengerName = (trip) =>
  trip.nome_passeggero || `${trip.nome || ''} ${trip.cognome || ''}`.trim();

export function formatDailyTrips(orders, { date } = {}) {
  const groupedOrders = {};
  const result = [];

  for (const originalOrder of orders) {
    const order = { ...originalOrder }; // ✅ clone per sicurezza
    const ownGroupId = date ? effectiveGroupId(order, date) : order.group_id;

    let groupId;
    if (ownGroupId) {
      groupId = ownGroupId;
    } else if (order.isGroup) {
      // Legacy CDI: no group_id sul documento, deriva la chiave da autista+company+orario
      groupId = `cdi_${order.autista || ''}_${order.company || ''}_${order.orario_presa || ''}`;
    } else {
      groupId = 'no_group';
    }

    if (groupId === 'no_group') {
      order.isGroup = false;
      result.push(order);
    } else {
      if (!groupedOrders[groupId]) {
        groupedOrders[groupId] = [];
      }

      // Cloniamo anche qui per evitare mutazioni successive
      groupedOrders[groupId].push({ ...order });
    }
  }

  for (const [groupId, group] of Object.entries(groupedOrders)) {
    group.sort((a, b) => {
      const aTime =
        parseInt((typeof a.orario_presa === 'string' ? a.orario_presa : '').replace(':', ''), 10) ||
        0;
      const bTime =
        parseInt((typeof b.orario_presa === 'string' ? b.orario_presa : '').replace(':', ''), 10) ||
        0;
      return aTime - bTime;
    });

    const FMT = 'HH:mm';

    // OCC-13 — stessa regola dei giri in pooling sugli ordini: il giro va dalla
    // prima presa all'ultima discesa pianificata, non dall'ultima PRESA (che
    // scartava l'ultima tratta del giro), più l'overhead di salita/discesa.
    const collapsedTemplate = collapseGroupLegs(
      group.map((t) => ({
        id: t.id,
        start_ms: dayjs(t.orario_presa, FMT).valueOf(),
        tempo: Number(t.tempo) || 0,
        fermo_auto: Number(t.fermo_auto) || 0,
      }))
    );
    const templateRideMin = collapsedTemplate ? groupDurationParts(collapsedTemplate).rideMin : 0;

    const mergedOrder = {
      isGroup: true,
      id: groupId,
      ids: group.map((o) => o.id),
      // I membri in chiaro, non solo i loro id. `nome_passeggero` è una stringa
      // unica ("Rossi, Bianchi, Verdi") e serve a scriverci sopra l'item; chi
      // deve far *scegliere* un membro — togliere un viaggio dal giro — ha
      // bisogno della riga per riga, e ricomporla spezzando la stringa
      // sbaglierebbe al primo nome con la virgola.
      members: group.map((o) => ({
        id: o.id,
        name: dailyTripPassengerName(o),
        time: o.orario_presa || '',
        from: o.partenza || '',
        to: o.arrivo || '',
      })),

      group_id: groupId,
      nome_passeggero: group.map(dailyTripPassengerName).filter(Boolean).join(', '),
      prezzo:
        group
          .reduce((sum, o) => {
            let p = parsePrice(o.prezzo);
            return sum + p;
          }, 0)
          .toFixed(2) + ' €',
      fermo_auto: group.reduce((sum, o) => sum + (parseFloat(o.fermo_auto) || 0), 0),
      fermo_auto_incluso: group.reduce(
        (sum, o) => sum + (parseFloat(o.fermo_auto_incluso) || 0),
        0
      ),
      orario_presa: group[0].orario_presa,
      partenza: group[0].partenza,
      arrivo: group[group.length - 1].arrivo,
      // In SECONDI, come per gli ordini: i consumatori leggono `tempo` col
      // doppio formato di OCC-1. Il fermo mezzo resta fuori (è sommato a parte
      // in `fermo_auto` e lo aggiungono i consumatori).
      tempo: Math.round(templateRideMin * 60),
      giorni: group[0].giorni,
      attivo: true,
      // Periodo di validità del giro = UNIONE dei periodi dei membri: il gruppo
      // gira finché almeno un passeggero è in servizio. Senza questi due campi
      // il merge li perdeva (è una whitelist) e ogni giro in pooling risultava
      // "valido sempre" — i giri scaduti restavano sulla timeline per sempre.
      ...mergeValidityRange(group),
      source: 'GRUPPO',
      company: group[0].company,
      id_gestore: group[0].id_gestore,
      indirizzi_da: group.map((o) => o.partenza),
      indirizzi_a: group.map((o) => o.arrivo),
      latlng_da: group[0].latlng_da,
      latlng_a: group[group.length - 1].latlng_a,
      waypoints_da: group.map((o) => ({
        address: o.partenza,
        lat: o.latlng_da?.latitude || o.latlng_da?.lat,
        lng: o.latlng_da?.longitude || o.latlng_da?.lng,
      })),
      waypoints_a: group.map((o) => ({
        address: o.arrivo,
        lat: o.latlng_a?.latitude || o.latlng_a?.lat,
        lng: o.latlng_a?.longitude || o.latlng_a?.lng,
      })),
    };
    if (group[0].company) {
      mergedOrder.company = group[0].company;
    }
    const groupAutista = getUnanimousValue(group, 'autista');
    if (groupAutista) {
      mergedOrder.autista = groupAutista;
    }
    const groupIdAutista = getUnanimousValue(group, 'id_autista');
    if (groupIdAutista) {
      mergedOrder.id_autista = groupIdAutista;
    }

    // Doppio operatore del giro, con la stessa regola dell'autista: vale se è
    // unanime fra i membri. Senza questi campi sull'oggetto sintetico,
    // `duplicate2op` non vedrebbe mai il secondo operatore di un giro e la
    // Timeline non ne disegnerebbe lo slot.
    for (const field of [
      'accompagnatore_richiesto',
      'secondo_operatore',
      'secondo_operatore_arrivo',
    ]) {
      const value = getUnanimousValue(group, field);
      if (value) mergedOrder[field] = value;
    }

    // Riporta le eccezioni per data dei singoli membri: un override di gruppo
    // (saveGroupOverride) scrive lo stesso `_overrides.{data}` su ogni doc del
    // gruppo, quindi basta fondere le mappe per data per farle vedere alla
    // Timeline anche sull'evento sintetico "daily-trip-group".
    const mergedOverrides = {};
    for (const o of group) {
      if (o._overrides) {
        for (const [dateKey, fields] of Object.entries(o._overrides)) {
          mergedOverrides[dateKey] = { ...mergedOverrides[dateKey], ...fields };
        }
      }
    }
    if (Object.keys(mergedOverrides).length > 0) {
      mergedOrder._overrides = mergedOverrides;
    }

    result.push(mergedOrder);
  }

  return result;
}

export function formatOrders(orders) {
  const groupedOrders = {};

  for (const order of orders) {
    const clonedOrder = { ...order };
    // A second-operator slot (`duplicate2op`) is a clone of its order, not one
    // more passenger: merged by `group_id` it would turn a one-trip group into
    // a three-member one. It stays a standalone item on the operator's row.
    const groupId = (!clonedOrder.op2_slot && clonedOrder.group_id) || 'no_group';

    if (groupId === 'no_group') {
      clonedOrder.isGroup = false;
    }

    if (!groupedOrders[groupId]) {
      groupedOrders[groupId] = [];
    }

    if (!clonedOrder.arrival_time_to_start) {
      clonedOrder.arrival_time_to_start = null;
    }

    if (!clonedOrder.arrival_time_to_end) {
      clonedOrder.arrival_time_to_end = null;
    }

    groupedOrders[groupId].push(clonedOrder);
  }

  const resultOrders = groupedOrders['no_group'] || [];
  delete groupedOrders['no_group'];

  for (const groupId of Object.keys(groupedOrders)) {
    const group = groupedOrders[groupId];
    group.sort((a, b) => toMoment(a.data_esecuzione).diff(toMoment(b.data_esecuzione)));

    // OCC-13 (docs/architecture/driver-occupancy.md) — la durata di un giro in
    // pooling è una regola condivisa con le analytics: dallo span degli orari di
    // presa (prima presa → ultima discesa pianificata) più l'overhead di
    // salita/discesa. `collapseGroupLegs` la calcola sulla forma normalizzata,
    // qui si adatta il doc Firestore.
    //
    // Il risultato si scrive in `tempo` come SECONDI: tutti i consumatori
    // leggono quel campo col doppio formato di OCC-1 (> 180 ⇒ secondi), quindi
    // così ognuno ne ricava i minuti esatti. La versione precedente sommava
    // minuti di span e secondi di marcia nello stesso numero, e la divisione
    // per 60 a valle riduceva span e overhead a un ventesimo del loro valore.
    const collapsed = collapseGroupLegs(
      group.map((o) => ({
        id: o.id,
        start_ms: toMoment(o.data_esecuzione).valueOf(),
        tempo: Number(o.tempo) || 0,
        fermo_auto: Number(o.fermo_auto) || 0,
        distance: Number(o.distanza_num) || 0,
        status: o.status,
      }))
    );
    // Il fermo mezzo lo somma già `mergedOrder.fermo_auto` e i consumatori lo
    // aggiungono al `tempo`: qui serve la sola parte di giro (`rideMin`), o si
    // conterebbe due volte.
    const rideMin = collapsed ? groupDurationParts(collapsed).rideMin : 0;
    const totalTime = Math.round(rideMin * 60);
    // Applied route (`applyGroupSchedule`): every member carries its planned
    // drop-off in `route`. The last one is where the run really ends; the
    // first pickup is already `data_esecuzione`. Partial schedules don't count.
    const dropoffs = group.map((o) => toMomentStrict(o.route?.dropoff_at));
    const scheduleEndMs = dropoffs.every(Boolean)
      ? Math.max(...dropoffs.map((d) => d.valueOf()))
      : null;

    const mergedOrder = {
      id: groupId,
      group_id: groupId,
      ids: group.map((o) => o.id),
      // Vedi la nota gemella sui pianificati: i membri in chiaro servono a chi
      // deve farne scegliere uno.
      members: group.map((o) => ({
        id: o.id,
        name: o.passeggero || `${o.nome || ''} ${o.cognome || ''}`.trim(),
        time: toMoment(o.data_esecuzione).isValid()
          ? toMoment(o.data_esecuzione).format('HH:mm')
          : '',
        from: o.indirizzo_da || '',
        to: o.indirizzo_a || '',
      })),
      status: group[0].status,
      statuses: group.map((o) => o.status),
      tempo: totalTime,
      schedule_end_ms: scheduleEndMs,
      isGroup: true,
      passeggero: group.map((o) => o.passeggero || `${o.nome} ${o.cognome}`).join(', '),
      distanza: group.reduce((sum, o) => sum + parseFloat(o.distanza_num || 0), 0),
      prezzo: group.reduce((sum, o) => {
        if (o.status === 6) return sum;
        const p = parsePrice(o.prezzo);
        return sum + p;
      }, 0),
      fermo_auto: group.reduce((sum, o) => sum + (parseFloat(o.fermo_auto) || 0), 0),
      fermo_auto_incluso: group.reduce(
        (sum, o) => sum + (parseFloat(o.fermo_auto_incluso) || 0),
        0
      ),

      indirizzo_da: group[0].indirizzo_da,
      indirizzi_da: group.map((o) => o.indirizzo_da),
      indirizzo_a: group[group.length - 1].indirizzo_a,
      indirizzi_a: group.map((o) => o.indirizzo_a),
      latlng_da: group[0].latlng_da,
      latlng_a: group[group.length - 1].latlng_a,
      waypoints_da: group.map((o) => ({
        address: o.indirizzo_da,
        lat: o.latlng_da?.latitude || o.latlng_da?.lat,
        lng: o.latlng_da?.longitude || o.latlng_da?.lng,
      })),
      waypoints_a: group.map((o) => ({
        address: o.indirizzo_a,
        lat: o.latlng_a?.latitude || o.latlng_a?.lat,
        lng: o.latlng_a?.longitude || o.latlng_a?.lng,
      })),

      data_esecuzione: group[0].data_esecuzione,
      data_inserimento: group[0].data_inserimento,
      company: group[0].company,
      source: 'GRUPPO',
      ar: false,
      id_autista: getUnanimousValue(group, 'id_autista'),
      id_gestore: group[0].id_gestore,
      arrival_time_to_start: group[0].arrival_time_to_start,
      arrival_time_to_end: group[group.length - 1].arrival_time_to_end,
      // Real execution times of the group ride: the group itself is a
      // client-side merge (no backend document of its own), so its real
      // times must be borrowed from the member legs while they're still
      // available here — timelineDataHandlers only ever sees this merged
      // object. Departure = first member that has one; arrival = the LAST
      // leg's completion, falling back to the SECOND-TO-LAST when the
      // driver app leaves the true last leg dangling (a known gap).
      lastLegOrder: group[group.length - 1],
      penultimateLegOrder: group.length > 1 ? group[group.length - 2] : null,
      firstLegOrder: group[0],
      // Raw member orders: billing per km prices each leg on its own
      // (`orderBillingValue`), and the summed `distanza` mixes m and km.
      legOrders: group,
    };

    resultOrders.push(mergedOrder);
  }

  return resultOrders;
}

export async function getIdGestore(company) {
  return new Promise((resolve) => {
    if (company) {
      window.firebase
        .firestore()
        .collection('gestori')
        .where('company', '==', company)
        .get()
        .then((snap) => {
          if (!snap.empty) {
            const doc = snap.docs[0];
            resolve(doc.id);
          } else {
            resolve(null);
          }
        })
        .catch(() => {
          resolve(null);
        });
    } else {
      resolve(null);
    }
  });
}

export async function geocode(search) {
  return new Promise((resolve, reject) => {
    window.firebase
      .auth()
      .currentUser.getIdToken(true)
      .then((token) => {
        doGet('/maps/geocode?query=' + encodeURIComponent(search))
          .then(resolve)
          .catch((err) => {
            console.error('Geocode request failed:', err);
            reject(err);
          });
      })
      .catch((err) => {
        console.error('Failed to get Firebase token:', err);
        reject(err);
      });
  });
}

export function statusText(status) {
  if (typeof status == 'string') {
    status = parseInt(status);
  }
  let statusStr = 'Stato sconosciuto';
  if (status == 1) {
    statusStr = 'Da eseguire';
  } else if (status == 2) {
    statusStr = 'Da eseguire';
  } else if (status == 3) {
    statusStr = 'Da eseguire';
  } else if (status == 4) {
    statusStr = 'Vicino casa';
  } else if (status == 5) {
    statusStr = 'Completato';
  } else if (status == 6) {
    statusStr = 'Annullato';
  } else if (status == 7) {
    statusStr = 'A bordo';
  } else if (status == 10) {
    statusStr = 'Completato manualmente';
  } else if (status == 12) {
    statusStr = 'In attesa (fermo auto)';
  } else if (status == 13) {
    statusStr = 'Rientro';
  } else if (status == 101) {
    statusStr = 'Rifiutato';
  }
  return statusStr;
}
